import { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Hide, Show } from "shared/components";
import { capitalize, formatToCurrency } from "shared/utils";
import { DiscountInfoTooltipProps } from "../types";
import { formatDiscountAmountForTooltip } from "../utils/discountCalculations";
import moment from "moment";
import { getDiscountCodeName } from "utils/discount-codes";

export const DiscountInfoTooltip = ({
  discountType,
  discountAmountInCents,
  discountCode,
  appliedBy,
  appliedAt,
  isCustom,
  endDate,
  discount,
  baseFeeInCents,
  children,
  showPercentageAsAmount,
  isInvitee,
  onlyContent = false
}: DiscountInfoTooltipProps) => {
  // Use discount object if provided, otherwise use individual props
  const activeDiscount = discount || {
    discount_type: discountType,
    discount_amount: discountAmountInCents,
    code: discountCode,
    end_date: endDate
  };

  const discountAmount = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!activeDiscount) return "";

    const baseFee = baseFeeInCents || 0;
    const amount = activeDiscount.discount_amount || 0;

    if (activeDiscount.discount_type === "flat")
      return formatToCurrency(
        { cents: amount, currency: "USD" },
        { minimumFractionDigits: 2 }
      );

    if (activeDiscount.discount_type === "percentage") {
      const discountAmount = Math.round(baseFee * (amount / 100));
      return formatToCurrency(
        { cents: discountAmount, currency: "USD" },
        { minimumFractionDigits: 2 }
      );
    }

    if (activeDiscount.discount_type === "free")
      return formatToCurrency(
        { cents: baseFee, currency: "USD" },
        { minimumFractionDigits: 2 }
      );

    return formatToCurrency(
      { cents: amount, currency: "USD" },
      { minimumFractionDigits: 2 }
    );
  }, [activeDiscount, baseFeeInCents]);

  const formattedDiscountType = useMemo(() => {
    return capitalize(activeDiscount.discount_type || "");
  }, [activeDiscount.discount_type, discountType]);

  // If no discount, return null
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!activeDiscount) return null;

  if (onlyContent)
    return (
      <ul className="space-y-2 list-disc list-inside text-white font-medium">
        <Show if={isCustom}>
          <li>Custom discount</li>
        </Show>
        <li>Discount code: {getDiscountCodeName(activeDiscount.code || "")}</li>
        <li>Discount type: {formattedDiscountType}</li>
        <Hide if={isInvitee}>
          <li>
            Discount:{" "}
            {formatDiscountAmountForTooltip(
              activeDiscount,
              showPercentageAsAmount
            )}
          </li>
        </Hide>
        <li>
          End date:{" "}
          {activeDiscount.end_date
            ? moment(activeDiscount.end_date).format("ll")
            : "No end date"}
        </li>
        <Show if={appliedBy}>
          <li>Applied by: {appliedBy}</li>
        </Show>
        <Show if={appliedAt}>
          <li>
            Applied on: {moment(appliedAt).format("MMM DD, YYYY [at] h:mm A")}
          </li>
        </Show>
      </ul>
    );

  return (
    <Tooltip>
      <TooltipTrigger>
        {children || (
          <span className="text-sm text-danger-600 cursor-pointer">
            -{discountAmount}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-[300px] w-full rounded-lg bg-alpha-800 p-4 text-xs text-white z-[50] shadow-md">
        <ul className="space-y-2 list-disc list-inside text-white font-medium">
          <Show if={isCustom}>
            <li>Custom discount</li>
          </Show>
          <li>Discount type: {formattedDiscountType}</li>
          <Hide if={isInvitee}>
            <li>
              Discount:{" "}
              {formatDiscountAmountForTooltip(
                activeDiscount,
                showPercentageAsAmount
              )}
            </li>
          </Hide>
          <li>
            End date:{" "}
            {activeDiscount.end_date
              ? moment(activeDiscount.end_date).format("ll")
              : "No end date"}
          </li>
          <Show if={appliedBy}>
            <li>Applied by: {appliedBy}</li>
          </Show>
          <Show if={appliedAt}>
            <li>
              Applied on: {moment(appliedAt).format("MMM DD, YYYY [at] h:mm A")}
            </li>
          </Show>
        </ul>
      </TooltipContent>
    </Tooltip>
  );
};
